import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from 'services/axiosBaseQuery'
import {
  RegionsServicePointRequest,
  RegionsServicePointResponse,
  StaticTourDeleteRequest,
  StaticTourDeleteResponse,
  StaticTourEditDetailRequest,
  StaticTourEditDetailResponse,
  StaticToursRequest,
  StaticToursResponse,
  StaticTourUpsertRequest,
  StaticTourUpsertResponse,
  StaticTourDetailRequest,
  StaticTourDetailResponse,
  KMLImportResponse,
  KMLImportRequest,
  SpItineraryListResponse,
  SpItineraryListRequest,
  MutationResponse,
  ImportSpItineraryListRequest,
  ImportSpItineraryListResponse,
  CalculateEtaRequest,
  CalculateEtaResponse,
  OptimizeServiceRequest,
  OptimizeServiceResponse,
  DrawPathRequest,
  DrawPathResponse,
  SpItineraryItemEditRequest,
  StaticTourAllKmlListRequest,
  StaticTourAllKmlListResponse,
  type GetStaticTourByRegionsResponse,
  type GetStaticTourByRegionsRequest,
  type SyncStaticTourResponse,
  type SyncStaticTourRequest,
} from './types'
import { sendNotification } from 'components'
import type { HistoryRequest, HistoryResponse } from 'containers/HistoryV2/types'

export const staticTourApi = createApi({
  reducerPath: 'staticTourApi',
  tagTypes: [
    'StaticTours',
    'StaticTourDetail',
    'SpItineraryList',
    'KmlList',
    'StaticTourSequences',
    'ServicePointsByRegion',
    'StaticTourHistory',
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/api/'}`,
  }),
  endpoints: (builder) => ({
    getStaticTours: builder.query<StaticToursResponse, StaticToursRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/static_tour/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['StaticTours'],
    }),
    getRegionsServicePoints: builder.query<RegionsServicePointResponse, RegionsServicePointRequest>(
      {
        query: ({ clientId, ...data }) => ({
          url: `clients/${clientId}/ops_management/static_tour/get_service_point_list_by_regions/`,
          method: 'POST',
          data,
        }),
        providesTags: ['ServicePointsByRegion'],
      },
    ),
    getEditStaticTourDetail: builder.query<
      StaticTourEditDetailResponse,
      StaticTourEditDetailRequest
    >({
      query: ({ clientId, id }) => ({
        url: `clients/${clientId}/ops_management/edit_static_tour_detail/${id}/`,
        method: 'GET',
      }),
      providesTags: ['StaticTourDetail'],
    }),
    addStaticTour: builder.mutation<StaticTourUpsertResponse, StaticTourUpsertRequest>({
      query({ clientId, ...data }) {
        return {
          url: `clients/${clientId}/ops_management/create_static_tour/`,
          method: 'POST',
          data,
        }
      },
      invalidatesTags: ['StaticTours'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editStaticTour: builder.mutation<StaticTourUpsertResponse, StaticTourUpsertRequest>({
      query({ clientId, id, ...data }) {
        return {
          url: `clients/${clientId}/ops_management/edit_static_tour/${id}/`,
          method: 'POST',
          data,
        }
      },
      invalidatesTags: ['StaticTours', 'StaticTourDetail', 'StaticTourHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteStaticTour: builder.mutation<StaticTourDeleteResponse, StaticTourDeleteRequest>({
      query({ clientId, ...data }) {
        return {
          url: `clients/${clientId}/ops_management/static_tour/`,
          method: 'DELETE',
          data,
        }
      },
      invalidatesTags: ['StaticTours', 'StaticTourDetail', 'StaticTourHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getStaticTour: builder.query<StaticTourDetailResponse, StaticTourDetailRequest>({
      query: ({ clientId, static_tour_id }) => ({
        url: `clients/${clientId}/ops_management/static_tour/get_static_tour_detail/`,
        method: 'GET',
        params: {
          static_tour_id,
        },
      }),
      providesTags: ['StaticTourDetail'],
    }),
    importKml: builder.mutation<KMLImportResponse, KMLImportRequest>({
      query: ({ clientId, id, body }) => ({
        url: `clients/${clientId}/ops_management/static_tour/${id}/upload_kml/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['StaticTourDetail', 'StaticTourHistory'],
    }),
    calculateEta: builder.mutation<CalculateEtaResponse, CalculateEtaRequest>({
      query: ({ clientId, id }) => ({
        url: `clients/${clientId}/ops_management/static_tour/${id}/calculate_eta/`,
        method: 'GET',
      }),
      invalidatesTags: ['SpItineraryList', 'StaticTourHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    optimizeService: builder.mutation<OptimizeServiceResponse, OptimizeServiceRequest>({
      query: ({ clientId, id }) => ({
        url: `clients/${clientId}/ops_management/static_tour/${id}/optimize_route/`,
        method: 'GET',
      }),
      invalidatesTags: ['SpItineraryList', 'StaticTourDetail'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    drawPathService: builder.mutation<DrawPathResponse, DrawPathRequest>({
      query: ({ clientId, id }) => ({
        url: `clients/${clientId}/ops_management/static_tour/${id}/draw_path/`,
        method: 'GET',
      }),
      invalidatesTags: ['StaticTourDetail', 'KmlList', 'StaticTourHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getItineraryList: builder.query<SpItineraryListResponse, SpItineraryListRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/static_tour/itinerary_list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['SpItineraryList'],
    }),
    importSpItineraryList: builder.mutation<
      ImportSpItineraryListResponse,
      ImportSpItineraryListRequest
    >({
      query: ({ clientId, id: _id, body }) => ({
        url: `clients/${clientId}/import/static_tour_itinerary_list/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['SpItineraryList', 'StaticTourHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editSpItineraryItem: builder.mutation<MutationResponse, SpItineraryItemEditRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/ops_management/static_tour/edit_static_tour_itinerary/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'SpItineraryList',
        'StaticTourDetail',
        'StaticTourSequences',
        'StaticTourHistory',
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getAllKMLs: builder.query<StaticTourAllKmlListResponse, StaticTourAllKmlListRequest>({
      query: ({ clientId }) => ({
        url: `clients/${clientId}/ops_management/static_tour/kml_files/`,
        method: 'GET',
      }),
      providesTags: ['KmlList'],
    }),
    getStaticTourSequence: builder.query<
      Array<{
        id: number
        sequence: number
        service_point: number
      }>,
      {
        clientId: number
        staticTourId: number
      }
    >({
      query: ({ clientId, staticTourId }) => ({
        url: `clients/${clientId}/ops_management/static_tour/itinerary/detail/?static_tour_id=${staticTourId}`,
        method: 'GET',
      }),
      providesTags: ['StaticTourSequences'],
    }),
    clearStaticTourItinerarySequences: builder.mutation<
      MutationResponse,
      {
        clientId: number
        staticTourId: number
        service_point_type?: string
        service_point_id?: number
      }
    >({
      query: ({ clientId, staticTourId, service_point_type, service_point_id }) => ({
        url: `clients/${clientId}/ops_management/static_tour/${staticTourId}/itinerary/clear_sequence/`,
        method: 'PATCH',
        body: {
          service_point_id,
          service_point_type,
        },
      }),
      invalidatesTags: ['StaticTourSequences', 'SpItineraryList', 'StaticTourHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {}
      },
    }),
    getStaticTourByRegions: builder.query<
      GetStaticTourByRegionsResponse,
      GetStaticTourByRegionsRequest
    >({
      query: ({ clientId, regionIds }) => ({
        url: `clients/${clientId}/ops_management/static_tour/retrieve/`,
        method: 'GET',
        params: {
          region_id_list: regionIds ? `[${regionIds.join(',')}]` : undefined,
        },
      }),
      transformResponse: (res: GetStaticTourByRegionsResponse, _, { staticTourId }) =>
        res.filter((item) => item.value !== staticTourId),
    }),
    syncStaticTours: builder.mutation<SyncStaticTourResponse, SyncStaticTourRequest>({
      query: ({ clientId, staticTourId, ...data }) => ({
        url: `clients/${clientId}/ops_management/static_tour/duplicate/${staticTourId}/`,
        method: 'POST',
        data,
        errorOverride: true,
      }),
      invalidatesTags: ['KmlList', 'StaticTourDetail', 'StaticTourSequences'],
    }),
    getStaticTourHistory: builder.query<
      HistoryResponse & MutationResponse,
      HistoryRequest & { clientId: number; staticTourId: number }
    >({
      query: ({ clientId, staticTourId, ...data }) => ({
        url: `clients/${clientId}/ops_management/static_tour/${staticTourId}/history/`,
        method: 'POST',
        data,
      }),
      providesTags: ['StaticTourHistory'],
    }),
  }),
})

export const {
  useGetStaticToursQuery,
  useGetRegionsServicePointsQuery,
  useGetEditStaticTourDetailQuery,
  useAddStaticTourMutation,
  useDeleteStaticTourMutation,
  useEditStaticTourMutation,
  useGetStaticTourQuery,
  useImportKmlMutation,
  useGetItineraryListQuery,
  useImportSpItineraryListMutation,
  useEditSpItineraryItemMutation,
  useCalculateEtaMutation,
  useOptimizeServiceMutation,
  useDrawPathServiceMutation,
  useGetAllKMLsQuery,
  useGetStaticTourSequenceQuery,
  useClearStaticTourItinerarySequencesMutation,
  useGetStaticTourByRegionsQuery,
  useSyncStaticToursMutation,
  useGetStaticTourHistoryQuery,
} = staticTourApi
